/**
 * Single source for building `?redirect_url=` auth links (#1774).
 *
 * Deliberately a neutral, non-"use client" module: server components (the
 * login page's `signUpUrl`, the team-join redirect) and client components
 * (AuthStepGuard, AttributedSignUp, checkout) all build the same shape, and
 * the client-marked guard module can't be imported from a server component.
 *
 * Callers are responsible for the target being a SAFE INTERNAL path
 * (firstSafeAuthRedirect / safeInternalPath) — this helper only encodes.
 */
export function withRedirectUrl(
  basePath: string,
  redirectTarget: string | null | undefined,
): string {
  return redirectTarget
    ? `${basePath}?redirect_url=${encodeURIComponent(redirectTarget)}`
    : basePath;
}

/**
 * Build the first-party login URL for a signed-out protected page request.
 *
 * Clerk's default middleware redirect serialises the whole request as an
 * absolute `redirect_url`. Our login page intentionally accepts only internal
 * paths, so that absolute value is rejected and the user falls back to the
 * dashboard. Strip the origin here, at the request chokepoint, and carry only
 * the path + query the browser actually asked for.
 *
 * The origin is deliberately ignored even when it is attacker-controlled.
 * Fragments are not included because browsers never send them to middleware.
 */
export function protectedPageLoginUrl(
  requestUrl: Pick<URL, "pathname" | "search">,
): string {
  return withRedirectUrl(
    "/login",
    `${requestUrl.pathname}${requestUrl.search}`,
  );
}
